import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import Layout from './components/shared/Layout';
import ToastProvider from './context/ToastProvider';
import { UserContextProvider } from './context/UserProvider';
import ThemeProvider from './context/ThemeProvider';
import LayoutProvider from './context/LayoutProvider';
import RoutesWrapper from './route/RoutesWrapper';
import TokenLoginToastHandler from './components/TokenLoginToastHandler';

import 'react-quill/dist/quill.snow.css';
import 'rc-pagination/assets/index.css';
import { MeetingContextProvider } from './context/MeetingProvider';
import { PositionContextProvider } from './context/PositionProvider';
import { CustomFlowbiteTheme, Flowbite } from 'flowbite-react';
import { RecommendationsContextProvider } from './context/RecommendationsProvider';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import customTheme from './theme';

// Create a client
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			refetchOnMount: false,
			refetchOnReconnect: false,
			retry: false,
			staleTime: 5 * 60 * 1000, // 5 mins
		},
	},
});

function App() {
	useEffect(() => {
		if (process.env.NODE_ENV === 'development') return;
		// Save the original window.open function
		const originalWindowOpen = window.open;

		window.open = function (url: string | URL | undefined, target?: string, features?: string) {
			// Check if the URL is REACT_APP_ADMIN_URL, only then modify the URL
			if (
				!url?.toString().includes(process.env.REACT_APP_APP_ENDPOINT || '') &&
				!url?.toString().includes('https://') &&
				!url?.toString().includes('http://')
			) {
				// Define your prefix
				const urlPrefix = '/jobseeker';

				const modifiedUrl =
					typeof url === 'string'
						? url.startsWith(urlPrefix)
							? url
							: `${urlPrefix}${url}`
						: url instanceof URL
						  ? url.pathname.startsWith(urlPrefix)
								? url.href
								: `${url.origin}${urlPrefix}${url.pathname}${url.search}${url.hash}`
						  : url;

				return originalWindowOpen.call(window, modifiedUrl, target, features);
			}

			return originalWindowOpen.call(window, url, target, features);
		};

		const currentUrl = new URL(window.location.href);
		if (!currentUrl.pathname.startsWith('/jobseeker') && !currentUrl.pathname.startsWith('/university')) {
			window.location.href = '/jobseeker' + currentUrl.pathname;
		}
	}, []);

	return (
		<Flowbite theme={{ theme: customTheme }}>
			<QueryClientProvider client={queryClient}>
				<UserContextProvider>
					<BrowserRouter basename={process.env.NODE_ENV === 'development' ? '' : '/jobseeker'}>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<ThemeProvider>
								<PositionContextProvider>
									<RecommendationsContextProvider>
										<LayoutProvider>
											<MeetingContextProvider>
												<Layout>
													<ToastProvider>
														<TokenLoginToastHandler />
														<RoutesWrapper />
													</ToastProvider>
												</Layout>
											</MeetingContextProvider>
										</LayoutProvider>
									</RecommendationsContextProvider>
								</PositionContextProvider>
							</ThemeProvider>
						</LocalizationProvider>
					</BrowserRouter>
				</UserContextProvider>
			</QueryClientProvider>
		</Flowbite>
	);
}

export default App;
